import { Injectable } from '@angular/core';
import {HttpEvent, HttpInterceptor, HttpHandler, HttpRequest, HttpErrorResponse} from '@angular/common/http';
import { Observable } from 'rxjs/Rx';
import 'rxjs/add/observable/throw'
import 'rxjs/add/operator/catch';
import { retry, catchError } from 'rxjs/operators';
import { AuthenticationService } from 'pages/authentication/_services';
import { GlobalErrorHandler } from 'helpers/services/error-handler.service';
import { AuthenticationHelper } from 'helpers/authentication.helper';
import {BehaviorSubject} from 'rxjs/BehaviorSubject';

@Injectable()
export class HttpRequestInterceptor implements HttpInterceptor {
  private GET_TOKEN_PATH: string = '/uaa/oauth/token';
  private FORGOT_PASSWORD_PATH: string = '/users/password/reset';
  private OAUTH2_GRANT_TYPE: string = 'refresh_token';
  private ignorePaths = [this.GET_TOKEN_PATH, this.FORGOT_PASSWORD_PATH]

  private refreshTokenInProgress = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  constructor (
      public authenticationService: AuthenticationService,
      public globalErrorHandler: GlobalErrorHandler,
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(AuthenticationHelper.applyCredentials(request))
      .pipe(
        catchError((error: HttpErrorResponse) => {
          const isIgnorePath = this.ignorePaths.some(url => request.url.includes(url))

          // We don't want to refresh token for some requests like login or refresh token itself
          // So we verify url and we throw an error if it's the case
          if (isIgnorePath) {
            // We do another check to see if refresh token failed
            // In this case we want to logout user and to redirect it to login page
            if (request.body && request.body.includes(this.OAUTH2_GRANT_TYPE)) {
              this.authenticationService.logout()
            }

            return Observable.throw(error);
          }

          // If error status is different than 401 we want to skip refresh token
          // So we check that and throw the error if it's the case
          if (error.status !== 401) {
            return Observable.throw(error);
          }

          if (this.refreshTokenInProgress) {
            // If refreshTokenInProgress is true, we will wait until refreshTokenSubject has a non-null value
            // – which means the new token is ready and we can retry the request again
            return this.refreshTokenSubject
              .filter(result => result !== null)
              .take(1)
              .switchMap(() => next.handle(AuthenticationHelper.applyCredentials(request)));
          } else {
            this.refreshTokenInProgress = true;

            // Set the refreshTokenSubject to null so that subsequent API calls will wait until the new token has been retrieved
            this.refreshTokenSubject.next(null);

            // Call auth.refreshAccessToken(this is an Observable that will be returned)
            return this.authenticationService
              .refreshToken()
              .switchMap((token: any) => {
                // When the call to refreshToken completes we reset the refreshTokenInProgress to false
                // for the next time the token needs to be refreshed
                this.refreshTokenInProgress = false;
                this.refreshTokenSubject.next(token);

                return next.handle(AuthenticationHelper.applyCredentials(request));
              })
              .catch((error: any) => {
                this.refreshTokenInProgress = false;
                this.authenticationService.logout();

                return Observable.throw(error);
              });
          }
        })
      )
  }
}



// WEBPACK FOOTER //
// ./src/app/core/authentication/interceptor/http-request-interceptor.ts